import * as React from "react";
import {
    type ColumnDef,
    type ColumnFiltersState,
    type ColumnSizingState,
    type ExpandedState,
    type SortingState,
    type RowData,
    type Column,
    type Header,
    type Table,
    flexRender,
    getCoreRowModel,
    getExpandedRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import { Pagination, type TPaginationProps } from "@/ui/atoms/Pagination";
import * as Popover from "@radix-ui/react-popover";
import { DayPicker, type DateRange } from "react-day-picker";
import "react-day-picker/style.css";
import { cn, useDebounce } from "@/utils/utils";
import {
    isDateRangeFilterActive,
    normalizeDateRangeFilter,
    parseColumnDateRangeFilter,
    presetLast12Months,
    presetLast30Days,
    presetLastMonth,
    presetThisMonth,
} from "@/utils/dateRangeFilter";
import { STRING_MULTI_SELECT_BLANK } from "@/utils/tableMultiSelectFilter";
import { format, parse, startOfDay } from "date-fns";
import { ChevronUp, ChevronDown, ChevronsUpDown, X, Filter, Calendar } from "lucide-react";

// Extend TanStack Table's ColumnMeta to include filter metadata
declare module "@tanstack/react-table" {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    interface ColumnMeta<TData extends RowData, TValue> {
        filterType?: "text" | "select" | "multiSelect" | "date" | "boolean" | "numberRange";
        filterOptions?: { value: string; label: string }[];
        filterPlaceholder?: string;
        /**
         * Force the option-search box on for a multiSelect / numberRange filter
         * regardless of option count. Without this, the search box only appears
         * once a list exceeds OPTION_SEARCH_THRESHOLD. Set on key lookup columns
         * (file name, deal/submission name, business partner) so they're always
         * searchable.
         */
        filterSearchable?: boolean;
        /**
         * Show a pinned "(Blanks)" option on a `text` filter (checkbox above the
         * search box) that matches rows whose value is empty/null. multiSelect and
         * numberRange filters instead surface blanks via a `STRING_MULTI_SELECT_BLANK`
         * entry in `filterOptions`, which is auto-pinned to the top of the list.
         */
        filterBlankOption?: boolean;
        /** Per-column text filter uses this string instead of accessor output (e.g. displayed labels). */
        getSearchValue?: (row: TData) => string;
        /** Hide the inline text box under this column (e.g. action-only columns). */
        hidePerColumnTextSearch?: boolean;
        /** Extra classes for this column's header cell (e.g. to tint a column group). */
        headerClassName?: string;
    }
}

// Persisted column widths live under a per-table key so each user's layout
// survives reloads. localStorage is per-browser-profile, i.e. per user.
const COLUMN_SIZING_STORAGE_PREFIX = "proda.dataTable.columnSizing.";

/** "1,234", or "1,234+" for a count the server stopped at. */
function formatCount(n: number, isLowerBound: boolean): string {
    return `${n.toLocaleString()}${isLowerBound ? "+" : ""}`;
}

function loadColumnSizing(storageKey?: string): ColumnSizingState {
    if (!storageKey || typeof window === "undefined") return {};
    try {
        const raw = window.localStorage.getItem(COLUMN_SIZING_STORAGE_PREFIX + storageKey);
        if (!raw) return {};
        const parsed = JSON.parse(raw) as unknown;
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
            return parsed as ColumnSizingState;
        }
        return {};
    } catch {
        return {};
    }
}

function saveColumnSizing(storageKey: string, sizing: ColumnSizingState): void {
    if (typeof window === "undefined") return;
    try {
        const key = COLUMN_SIZING_STORAGE_PREFIX + storageKey;
        if (Object.keys(sizing).length === 0) {
            window.localStorage.removeItem(key);
        } else {
            window.localStorage.setItem(key, JSON.stringify(sizing));
        }
    } catch {
        // Ignore quota / serialization errors — persistence is best-effort.
    }
}

// Width a column needs to show its full header label plus its controls (sort /
// filter icons) and padding. Used by double-click-to-fit on the resize handle.
function measureHeaderFitWidth(
    tableEl: HTMLTableElement | null,
    columnId: string,
): number | undefined {
    if (!tableEl) return undefined;
    const th = tableEl.querySelector<HTMLTableCellElement>(
        `thead th[data-column-id="${columnId}"]`,
    );
    const content = th?.querySelector<HTMLElement>("[data-header-content]");
    const label = content?.querySelector<HTMLElement>("[data-header-label]");
    if (!content || !label) return undefined;
    // Natural label text width (scrollWidth survives truncation) + the other
    // controls + inter-element gaps; slack/empty space is ignored so an
    // over-widened column shrinks back to fit.
    const children = Array.from(content.children) as HTMLElement[];
    const othersWidth = children
        .filter((c) => c !== label)
        .reduce((sum, c) => sum + c.offsetWidth, 0);
    const gaps = Math.max(0, children.length - 1) * 4; // gap-1
    const CONTENT_PR = 8; // pr-2
    const TH_PX = 32; // px-4 both sides
    const BUFFER = 4;
    return Math.ceil(label.scrollWidth + othersWidth + gaps + CONTENT_PR + TH_PX + BUFFER);
}

export interface TDataTableProps<TData, TValue> {
    columns: ColumnDef<TData, TValue>[];
    data: TData[];
    onRowClick?: (row: TData) => void;
    onFilteredDataChange?: (rows: TData[]) => void;
    loading?: boolean;
    emptyMessage?: string;
    className?: string;
    enableFiltering?: boolean;
    enableColumnResizing?: boolean;
    /**
     * Stable identifier for this table. When set, user-adjusted column widths
     * are persisted to localStorage under this key and restored on next visit.
     */
    storageKey?: string;
    /** Max height of the table body; only the rows scroll, not the page. Omit to allow page scroll. */
    maxHeight?: string | number;
    /** Seed column filters on mount (e.g. restored from URL search params). */
    initialColumnFilters?: ColumnFiltersState;
    /** Called whenever column filters change, so callers can persist externally. */
    onColumnFiltersChange?: (filters: ColumnFiltersState) => void;
    /**
     * Controlled column filters (server mode). When provided, the table renders
     * exactly these filters every render. Omit for internal/uncontrolled state.
     */
    columnFilters?: ColumnFiltersState;
    /**
     * Extra header row: one debounced text input per searchable column (before funnel/column filters).
     * Uses accessor values, or `meta.getSearchValue` when provided.
     */
    enablePerColumnTextSearch?: boolean;
    perColumnTextPlaceholder?: string;
    pagination?: TPaginationProps;
    /** Explicit count for server mode (overrides the internally computed count). */
    rowCount?: {
        filtered: number;
        total: number;
        isFiltered: boolean;
        /** Render the count as "N+" (the server stopped counting at a cap). */
        filteredIsLowerBound?: boolean;
        totalIsLowerBound?: boolean;
    };
    /** Noun for the unfiltered count label. Default { one: "row", many: "rows" }. */
    rowNoun?: { one: string; many: string };
    /** Show the filtered-row-count bar above the table. Default true. */
    showRowCount?: boolean;
    /** Server mode: disable client-side row filtering (TanStack manualFiltering). */
    manualFiltering?: boolean;
    /** Server mode: disable client-side row sorting (TanStack manualSorting). */
    manualSorting?: boolean;
    /** Controlled sorting state for server mode. */
    sortingState?: SortingState;
    /** Called with the resolved sorting whenever it changes. */
    onSortingChange?: (sorting: SortingState) => void;
    /**
     * Nested rows for a row (TanStack `getSubRows`). Sub-rows render as ordinary
     * `<tr>`s using the same column defs and the same resolved widths, so they stay
     * aligned through a column resize — which a nested table inside a `colSpan`
     * cell cannot. They only enter the row model while their parent is expanded,
     * and never affect the server `rowCount`/`pagination` totals.
     */
    getSubRows?: (row: TData) => TData[] | undefined;
    /** Controlled expanded state; required for `getSubRows` to be reachable. */
    expanded?: ExpandedState;
    onExpandedChange?: (expanded: ExpandedState) => void;
    /** Extra classes for a row, by row data and nesting depth (0 = top level). */
    rowClassName?: (row: TData, depth: number) => string | undefined;
    /**
     * Footer row(s) (`<tr>`) rendered in a `<tfoot>` after the body. Unaffected
     * by pagination/filtering. Pinned to the bottom of the scroll area when
     * `maxHeight` is set.
     */
    footerRow?: React.ReactNode;
    /**
     * Full-width row rendered directly beneath an expanded row, before its
     * sub-rows. Use for content that should span every column (e.g. a notice).
     * Return null/undefined to render nothing.
     */
    renderRowBanner?: (row: TData) => React.ReactNode;
}

function stringifySearchCellValue(v: unknown): string {
    if (v == null) return "";
    if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") {
        return String(v);
    }
    return "";
}

function getSearchHaystackFromDef<TData>(row: TData, columnDef: ColumnDef<TData, unknown>): string {
    const meta = columnDef.meta as { getSearchValue?: (r: TData) => string } | undefined;
    if (meta?.getSearchValue) {
        return meta.getSearchValue(row);
    }
    if ("accessorFn" in columnDef && typeof columnDef.accessorFn === "function") {
        try {
            return stringifySearchCellValue(columnDef.accessorFn(row, 0));
        } catch {
            return "";
        }
    }
    if ("accessorKey" in columnDef && columnDef.accessorKey) {
        return stringifySearchCellValue(
            (row as Record<string, unknown>)[String(columnDef.accessorKey)],
        );
    }
    return "";
}

function columnHasInlineTextSearch<TData>(columnDef: ColumnDef<TData, unknown>): boolean {
    const meta = columnDef.meta as
        | { hidePerColumnTextSearch?: boolean; getSearchValue?: unknown }
        | undefined;
    if (meta?.hidePerColumnTextSearch) return false;
    if (meta?.getSearchValue) return true;
    if ("accessorFn" in columnDef && typeof columnDef.accessorFn === "function") return true;
    if ("accessorKey" in columnDef && columnDef.accessorKey) return true;
    return false;
}

function findColumnDefById<TData, TValue>(
    defs: ColumnDef<TData, TValue>[],
    tableColumnId: string,
): ColumnDef<TData, unknown> | undefined {
    return defs.find((c) => {
        const cid =
            ("id" in c && c.id) ||
            ("accessorKey" in c && typeof c.accessorKey === "string" ? c.accessorKey : undefined);
        return cid === tableColumnId;
    }) as ColumnDef<TData, unknown> | undefined;
}

function PerColumnTextInput({
    columnId,
    placeholder,
    onDebouncedChange,
}: {
    columnId: string;
    placeholder: string;
    onDebouncedChange: (columnId: string, value: string) => void;
}) {
    const [local, setLocal] = React.useState("");
    const debounced = useDebounce(local, 300);

    React.useEffect(() => {
        onDebouncedChange(columnId, debounced);
    }, [columnId, debounced, onDebouncedChange]);

    return (
        <input
            type="search"
            value={local}
            onChange={(e) => setLocal(e.target.value)}
            placeholder={placeholder}
            className="h-8 w-full min-w-[4rem] rounded border border-input bg-background px-2 text-xs ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            aria-label={`Filter ${columnId}`}
        />
    );
}

// Column resize handle component.
// Wide invisible hit-area pinned to the right edge of each header cell, with a
// small always-visible vertical grip bar so the affordance is discoverable.
function ColumnResizeHandle<TData>({
    header,
    table,
    tableRef,
    onResizeStart,
    onResetColumn,
}: {
    header: Header<TData, unknown>;
    table: Table<TData>;
    tableRef: React.RefObject<HTMLTableElement>;
    onResizeStart: () => void;
    onResetColumn: (columnId: string) => void;
}) {
    const isResizing = header.column.getIsResizing();

    // Before the first resize, columns are auto-sized to content. Seed every
    // column's current rendered width so switching to fixed layout doesn't snap
    // the rest of the table to default widths — only the dragged column moves.
    const seedSizesFromDom = React.useCallback(() => {
        const el = tableRef.current;
        if (!el || Object.keys(table.getState().columnSizing).length > 0) return;
        const seeded: Record<string, number> = {};
        el.querySelectorAll<HTMLTableCellElement>("thead th[data-column-id]").forEach((th) => {
            const id = th.getAttribute("data-column-id");
            if (id) seeded[id] = th.getBoundingClientRect().width;
        });
        if (Object.keys(seeded).length > 0) table.setColumnSizing(seeded);
    }, [table, tableRef]);

    const handleResizeStart = (e: React.MouseEvent | React.TouchEvent) => {
        // A deliberate grab — mark it so widths get persisted (hover alone won't).
        onResizeStart();
        seedSizesFromDom();
        header.getResizeHandler()(e);
    };

    return (
        <div
            // Seed widths on hover (fires before mousedown) so the dragged column
            // starts from its real width instead of snapping to the default.
            onMouseEnter={seedSizesFromDom}
            onMouseDown={handleResizeStart}
            onTouchStart={handleResizeStart}
            onDoubleClick={() => onResetColumn(header.column.id)}
            className={cn(
                "group/resize absolute right-0 top-0 z-10 flex h-full w-2 items-center justify-end",
                "cursor-col-resize select-none touch-none",
            )}
            title="Drag to resize, double-click to fit the header"
        >
            <span
                className={cn(
                    "w-0.5 rounded-full bg-border transition-all",
                    "group-hover/resize:bg-primary",
                    isResizing ? "h-full w-[2px] bg-primary" : "h-1/2",
                )}
            />
        </div>
    );
}

// Sort icon component
function SortIcon({
    direction,
    isActive,
}: {
    direction: false | "asc" | "desc";
    isActive: boolean;
}) {
    const Icon =
        direction === "asc" ? ChevronUp : direction === "desc" ? ChevronDown : ChevronsUpDown;
    return (
        <Icon
            className={cn(
                "h-3.5 w-3.5 transition-opacity",
                isActive
                    ? "opacity-100 text-primary"
                    : "opacity-0 group-hover:opacity-100 text-muted-foreground",
            )}
        />
    );
}

/**
 * Keep a column's filter value exactly as the popover set it.
 *
 * A column that names no `filterFn` gets one inferred from its first row's value,
 * and for a numeric column that is TanStack's `inNumberRange`, whose `autoRemove`
 * discards any value whose `[0]`/`[1]` are empty. Every value the range and date
 * popovers produce is an object (`{from, to, selected}` / `{from, to}`), so on a
 * numeric column — Row Number, Trannum, Line Number — the filter was thrown away
 * the instant it was typed: the input appeared to reject input entirely.
 *
 * Naming a filter fn stops that inference. This one is never called: it is only
 * installed in `manualFiltering` mode, where the server does the filtering and
 * TanStack keeps the row model unfiltered. Client-mode tables are left alone (none
 * currently pair a range popover with client-side filtering, and such a column
 * would need a real client-side implementation of the range semantics, not this).
 */
function keepFilterValue(): boolean {
    return true;
}

// Object-shaped filter values — the ones `inNumberRange.autoRemove` would drop.
const OBJECT_FILTER_TYPES = ["numberRange", "date"] as const;

function useRangeFilterFns<TData, TValue>(
    columns: ColumnDef<TData, TValue>[],
    manualFiltering: boolean | undefined,
): ColumnDef<TData, TValue>[] {
    return React.useMemo(() => {
        if (!manualFiltering) return columns;
        let changed = false;
        const mapped = columns.map((def) => {
            const filterType = def.meta?.filterType;
            const needsFilterFn =
                def.filterFn === undefined &&
                filterType !== undefined &&
                (OBJECT_FILTER_TYPES as readonly string[]).includes(filterType);
            if (!needsFilterFn) return def;
            changed = true;
            return { ...def, filterFn: keepFilterValue };
        });
        // Preserve identity when nothing needed wrapping — a fresh columns array
        // makes TanStack rebuild every column instance on each render.
        return changed ? mapped : columns;
    }, [columns, manualFiltering]);
}

// Faceted checkbox lists longer than this get a search box to filter the options.
// Short lists stay uncluttered (no search box). Tunable.
const OPTION_SEARCH_THRESHOLD = 8;

// Case-insensitive substring filter over option labels. Used by the multi-select
// and number-range checkbox lists to narrow long option lists as the user types.
function filterOptionsByQuery(
    options: { value: string; label: string }[],
    query: string,
): { value: string; label: string }[] {
    const q = query.trim().toLowerCase();
    if (q === "") return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
}

// Search input shown above a faceted checkbox list. Mirrors TextFilterContent's
// styling (plain input + X clear button).
function OptionSearchInput({
    value,
    onChange,
    placeholder,
    autoFocus = true,
}: {
    value: string;
    onChange: (next: string) => void;
    placeholder?: string;
    autoFocus?: boolean;
}) {
    return (
        <div className="relative shrink-0">
            <input
                type="text"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder ?? "Search options..."}
                className="w-full h-8 px-3 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/50 placeholder:text-muted-foreground/50"
                autoFocus={autoFocus}
            />
            {value && (
                <button
                    type="button"
                    onClick={() => onChange("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 hover:bg-muted rounded"
                    aria-label="Clear search"
                >
                    <X className="h-3 w-3 text-muted-foreground" />
                </button>
            )}
        </div>
    );
}

// Pull the "(Blanks)" sentinel option out of a faceted option list so callers
// can pin it to the top of the checkbox list (always visible, never hidden by
// the option search). Returns the blank option (if any) and the remainder.
function splitBlankOption(options: { value: string; label: string }[]): {
    blank: { value: string; label: string } | undefined;
    rest: { value: string; label: string }[];
} {
    const blank = options.find((o) => o.value === STRING_MULTI_SELECT_BLANK);
    return blank
        ? { blank, rest: options.filter((o) => o.value !== STRING_MULTI_SELECT_BLANK) }
        : { blank: undefined, rest: options };
}

// A single pinned "(Blanks)" checkbox row. Shared by the text, multiSelect and
// numberRange filters so the option looks and behaves identically everywhere.
function BlankOptionCheckbox({
    label,
    checked,
    disabled,
    onToggle,
}: {
    label: string;
    checked: boolean;
    disabled?: boolean;
    onToggle: () => void;
}) {
    return (
        <label className="flex cursor-pointer items-center gap-2 text-sm">
            <input
                type="checkbox"
                checked={checked}
                disabled={disabled}
                onChange={onToggle}
                className="border-input shrink-0 rounded"
            />
            <span className="min-w-0 flex-1 truncate italic text-muted-foreground" title={label}>
                {label}
            </span>
        </label>
    );
}

// Text filter component with debouncing
function TextFilterContent({
    column,
    placeholder,
    onClose,
    blankOption,
}: {
    column: Column<unknown, unknown>;
    placeholder?: string;
    onClose: () => void;
    // When true, show a pinned "(Blanks)" checkbox that filters empty/null values.
    blankOption?: boolean;
}) {
    const rawFilter = (column.getFilterValue() as string) ?? "";
    // `blank` is local state (set synchronously) rather than derived from the
    // filter value, so the debounce commit below can reliably skip while blanks
    // mode is on without racing the parent re-render.
    const [blank, setBlank] = React.useState(rawFilter === STRING_MULTI_SELECT_BLANK);
    const [value, setValue] = React.useState(
        rawFilter === STRING_MULTI_SELECT_BLANK ? "" : rawFilter,
    );
    const debouncedValue = useDebounce(value, 300);

    React.useEffect(() => {
        if (blank) return;
        const current = (column.getFilterValue() as string) ?? "";
        if ((debouncedValue || "") === current) return;
        column.setFilterValue(debouncedValue || undefined);
    }, [column, debouncedValue, blank]);

    // The popover unmounts on close, which cancels a pending debounce tick —
    // commit the latest typed value on unmount so a quick Done/click-away
    // doesn't silently drop the filter. Blanks mode is committed eagerly on
    // toggle, so skip it here.
    const valueRef = React.useRef(value);
    valueRef.current = value;
    const blankRef = React.useRef(blank);
    blankRef.current = blank;
    React.useEffect(
        () => () => {
            if (blankRef.current) return;
            const current = (column.getFilterValue() as string) ?? "";
            if (current === STRING_MULTI_SELECT_BLANK) return;
            if ((valueRef.current || "") !== current) {
                column.setFilterValue(valueRef.current || undefined);
            }
        },
        [column],
    );

    const toggleBlank = () => {
        if (blank) {
            setBlank(false);
            column.setFilterValue(undefined);
        } else {
            setBlank(true);
            setValue("");
            column.setFilterValue(STRING_MULTI_SELECT_BLANK);
        }
    };

    const onType = (next: string) => {
        if (blank) setBlank(false);
        setValue(next);
    };

    return (
        <div className="space-y-2">
            {blankOption && (
                <BlankOptionCheckbox label="(Blanks)" checked={blank} onToggle={toggleBlank} />
            )}
            <div className="relative">
                <input
                    type="text"
                    value={value}
                    onChange={(e) => onType(e.target.value)}
                    placeholder={placeholder ?? "Filter..."}
                    disabled={blank}
                    className="w-full h-8 px-3 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/50 placeholder:text-muted-foreground/50 disabled:cursor-not-allowed disabled:opacity-50"
                    autoFocus
                />
                {value && (
                    <button
                        onClick={() => onType("")}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 hover:bg-muted rounded"
                    >
                        <X className="h-3 w-3 text-muted-foreground" />
                    </button>
                )}
            </div>
            <div className="flex justify-end gap-2">
                <button
                    onClick={() => {
                        setBlank(false);
                        setValue("");
                        column.setFilterValue(undefined);
                    }}
                    className="px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
                >
                    Clear
                </button>
                <button
                    onClick={onClose}
                    className="px-3 py-1 text-xs bg-primary text-primary-foreground rounded hover:bg-primary/90"
                >
                    Done
                </button>
            </div>
        </div>
    );
}

// Select filter component
function SelectFilterContent({
    column,
    options,
    placeholder,
    onClose,
}: {
    column: Column<unknown, unknown>;
    options: { value: string; label: string }[];
    placeholder?: string;
    onClose: () => void;
}) {
    const value = (column.getFilterValue() as string) ?? "";

    return (
        <div className="space-y-2">
            <select
                value={value}
                onChange={(e) => column.setFilterValue(e.target.value || undefined)}
                className="w-full h-8 px-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/50 cursor-pointer"
                autoFocus
            >
                <option value="">{placeholder ?? "All"}</option>
                {options.map((option) => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </select>
            <div className="flex justify-end gap-2">
                <button
                    onClick={() => column.setFilterValue(undefined)}
                    className="px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
                >
                    Clear
                </button>
                <button
                    onClick={onClose}
                    className="px-3 py-1 text-xs bg-primary text-primary-foreground rounded hover:bg-primary/90"
                >
                    Done
                </button>
            </div>
        </div>
    );
}

/** Checkbox list: empty selection = show all rows; non-empty = keep rows whose value is in the selection (OR). */
function MultiSelectFilterContent({
    column,
    options,
    onClose,
    searchable,
}: {
    column: Column<unknown, unknown>;
    options: { value: string; label: string }[];
    onClose: () => void;
    searchable?: boolean;
}) {
    const raw = column.getFilterValue() as string[] | undefined;
    const selected = raw ?? [];
    const [optionQuery, setOptionQuery] = React.useState("");

    // The "(Blanks)" option is pinned to the top (always visible, never hidden by
    // the option search); only the remaining options feed the search + scroll list.
    const { blank, rest } = React.useMemo(() => splitBlankOption(options), [options]);

    // Columns flagged `filterSearchable` are always searchable (any non-empty list);
    // others get the search box only once the list exceeds the threshold.
    const showSearch = searchable ? rest.length > 0 : rest.length > OPTION_SEARCH_THRESHOLD;
    const visibleOptions = React.useMemo(
        () => (showSearch ? filterOptionsByQuery(rest, optionQuery) : rest),
        [showSearch, rest, optionQuery],
    );

    const isChecked = (value: string) => selected.includes(value);

    const toggle = (value: string) => {
        const nextSet = new Set(selected);
        if (nextSet.has(value)) {
            nextSet.delete(value);
        } else {
            nextSet.add(value);
        }
        const next = [...nextSet];
        column.setFilterValue(next.length === 0 ? undefined : next);
    };

    return (
        <div className="flex min-w-0 flex-col space-y-2">
            {showSearch && <OptionSearchInput value={optionQuery} onChange={setOptionQuery} />}
            {blank && (
                <BlankOptionCheckbox
                    label={blank.label}
                    checked={isChecked(blank.value)}
                    onToggle={() => toggle(blank.value)}
                />
            )}
            {rest.length > 0 && (
                <div className="flex max-h-[min(360px,55vh)] flex-col gap-2 overflow-y-auto overflow-x-hidden pr-1">
                    {visibleOptions.length === 0 ? (
                        <span className="py-1 text-sm text-muted-foreground">
                            No matching options
                        </span>
                    ) : (
                        visibleOptions.map((option) => (
                            <label
                                key={option.value}
                                className="flex cursor-pointer items-start gap-2 text-sm"
                            >
                                <input
                                    type="checkbox"
                                    checked={isChecked(option.value)}
                                    onChange={() => toggle(option.value)}
                                    className="border-input mt-0.5 shrink-0 rounded"
                                />
                                <span
                                    className="min-w-0 flex-1 break-words leading-snug"
                                    title={option.label}
                                >
                                    {option.label}
                                </span>
                            </label>
                        ))
                    )}
                </div>
            )}
            <div className="flex shrink-0 justify-end gap-2 pt-1">
                <button
                    type="button"
                    onClick={() => column.setFilterValue(undefined)}
                    className="px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
                >
                    Clear
                </button>
                <button
                    type="button"
                    onClick={onClose}
                    className="rounded bg-primary px-3 py-1 text-xs text-primary-foreground hover:bg-primary/90"
                >
                    Done
                </button>
            </div>
        </div>
    );
}

const PRESET_BTN =
    "rounded-md border border-border bg-muted/30 px-2 py-0.5 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground";

// From / to calendar days: native date inputs (type + pick) + range DayPicker + presets
function DateFilterContent({
    column,
    onClose,
}: {
    column: Column<unknown, unknown>;
    onClose: () => void;
}) {
    const { from, to } = parseColumnDateRangeFilter(column.getFilterValue());

    const commit = (next: { from: string; to: string }) => {
        column.setFilterValue(normalizeDateRangeFilter(next));
    };

    const selectedRange: DateRange | undefined = React.useMemo(() => {
        const fd = from ? parse(from, "yyyy-MM-dd", new Date()) : undefined;
        const td = to ? parse(to, "yyyy-MM-dd", new Date()) : undefined;
        if (!fd && !td) return undefined;
        if (fd && td) {
            const a = startOfDay(fd);
            const b = startOfDay(td);
            return a <= b ? { from: a, to: b } : { from: b, to: a };
        }
        if (fd) return { from: startOfDay(fd), to: undefined };
        if (td) return { from: undefined, to: startOfDay(td) };
        return undefined;
    }, [from, to]);

    const inputClass =
        "h-9 w-full rounded-md border border-input bg-background pl-8 pr-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50";

    return (
        <div className="space-y-3">
            <div className="flex flex-wrap gap-1">
                <button
                    type="button"
                    className={PRESET_BTN}
                    onClick={() => commit(presetThisMonth())}
                >
                    This month
                </button>
                <button
                    type="button"
                    className={PRESET_BTN}
                    onClick={() => commit(presetLastMonth())}
                >
                    Last month
                </button>
                <button
                    type="button"
                    className={PRESET_BTN}
                    onClick={() => commit(presetLast30Days())}
                >
                    Last 30 days
                </button>
                <button
                    type="button"
                    className={PRESET_BTN}
                    onClick={() => commit(presetLast12Months())}
                >
                    Last 12 months
                </button>
            </div>

            <div className="space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-medium text-muted-foreground">From</span>
                    {from ? (
                        <button
                            type="button"
                            onClick={() => commit({ from: "", to })}
                            className="text-xs text-muted-foreground hover:text-foreground"
                        >
                            Clear
                        </button>
                    ) : null}
                </div>
                <div className="relative">
                    <Calendar className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <input
                        type="date"
                        value={from}
                        onChange={(e) => commit({ from: e.target.value, to })}
                        className={inputClass}
                        aria-label="From date"
                    />
                </div>
            </div>

            <div className="space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-medium text-muted-foreground">To</span>
                    {to ? (
                        <button
                            type="button"
                            onClick={() => commit({ from, to: "" })}
                            className="text-xs text-muted-foreground hover:text-foreground"
                        >
                            Clear
                        </button>
                    ) : null}
                </div>
                <div className="relative">
                    <Calendar className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <input
                        type="date"
                        value={to}
                        onChange={(e) => commit({ from, to: e.target.value })}
                        className={inputClass}
                        aria-label="To date"
                    />
                </div>
            </div>

            <div className="flex justify-center overflow-x-auto pt-1">
                <DayPicker
                    mode="range"
                    selected={selectedRange}
                    onSelect={(r) => {
                        if (!r) {
                            commit({ from: "", to: "" });
                            return;
                        }
                        if (!r.from) {
                            commit({ from: "", to });
                            return;
                        }
                        const fromS = format(startOfDay(r.from), "yyyy-MM-dd");
                        const toS = r.to ? format(startOfDay(r.to), "yyyy-MM-dd") : "";
                        commit({ from: fromS, to: toS });
                    }}
                    showOutsideDays
                    className="text-sm [--rdp-day-height:2rem] [--rdp-day-width:2rem] [--rdp-day_button-height:1.875rem] [--rdp-day_button-width:1.875rem]"
                />
            </div>

            <div className="flex justify-end gap-2 border-t border-border pt-2">
                <button
                    type="button"
                    onClick={() => column.setFilterValue(undefined)}
                    className="px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
                >
                    Clear all
                </button>
                <button
                    type="button"
                    onClick={onClose}
                    className="rounded bg-primary px-3 py-1 text-xs text-primary-foreground hover:bg-primary/90"
                >
                    Done
                </button>
            </div>
        </div>
    );
}

// Boolean filter component
function BooleanFilterContent({
    column,
    placeholder,
    onClose,
}: {
    column: Column<unknown, unknown>;
    placeholder?: string;
    onClose: () => void;
}) {
    const value = column.getFilterValue();
    const stringValue = value === true ? "true" : value === false ? "false" : "";

    return (
        <div className="space-y-2">
            <select
                value={stringValue}
                onChange={(e) => {
                    const newValue = e.target.value;
                    if (newValue === "true") {
                        column.setFilterValue(true);
                    } else if (newValue === "false") {
                        column.setFilterValue(false);
                    } else {
                        column.setFilterValue(undefined);
                    }
                }}
                className="w-full h-8 px-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/50 cursor-pointer"
                autoFocus
            >
                <option value="">{placeholder ?? "All"}</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
            </select>
            <div className="flex justify-end gap-2">
                <button
                    onClick={() => column.setFilterValue(undefined)}
                    className="px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
                >
                    Clear
                </button>
                <button
                    onClick={onClose}
                    className="px-3 py-1 text-xs bg-primary text-primary-foreground rounded hover:bg-primary/90"
                >
                    Done
                </button>
            </div>
        </div>
    );
}

export interface TNumberRangeMultiSelectFilterValue {
    from: string;
    to: string;
    selected: string[];
}

function parseNumberRangeMultiSelectFilter(raw: unknown): TNumberRangeMultiSelectFilterValue {
    if (raw && typeof raw === "object") {
        const v = raw as Partial<TNumberRangeMultiSelectFilterValue>;
        return {
            from: v.from ?? "",
            to: v.to ?? "",
            selected: Array.isArray(v.selected) ? v.selected : [],
        };
    }
    return { from: "", to: "", selected: [] };
}

function isNumberRangeMultiSelectFilterActive(raw: unknown): boolean {
    const { from, to, selected } = parseNumberRangeMultiSelectFilter(raw);
    return from !== "" || to !== "" || selected.length > 0;
}

function NumberRangeMultiSelectFilterContent({
    column,
    options,
    onClose,
    searchable,
}: {
    column: Column<unknown, unknown>;
    options: { value: string; label: string }[];
    onClose: () => void;
    searchable?: boolean;
}) {
    const { from, to, selected } = parseNumberRangeMultiSelectFilter(column.getFilterValue());
    const [optionQuery, setOptionQuery] = React.useState("");

    // The "(Blanks)" option is pinned to the top of the value list (always visible,
    // never hidden by the option search); only the rest feeds the search + scroll list.
    const { blank, rest } = React.useMemo(() => splitBlankOption(options), [options]);

    // Columns flagged `filterSearchable` are always searchable (any non-empty list);
    // others get the search box only once the list exceeds the threshold.
    const showSearch = searchable ? rest.length > 0 : rest.length > OPTION_SEARCH_THRESHOLD;
    const visibleOptions = React.useMemo(
        () => (showSearch ? filterOptionsByQuery(rest, optionQuery) : rest),
        [showSearch, rest, optionQuery],
    );

    const commit = (next: Partial<TNumberRangeMultiSelectFilterValue>) => {
        const merged = { from, to, selected, ...next };
        if (merged.from === "" && merged.to === "" && merged.selected.length === 0) {
            column.setFilterValue(undefined);
        } else {
            column.setFilterValue(merged);
        }
    };

    const isChecked = (value: string) => selected.includes(value);

    const toggle = (value: string) => {
        const nextSet = new Set(selected);
        if (nextSet.has(value)) {
            nextSet.delete(value);
        } else {
            nextSet.add(value);
        }
        commit({ selected: [...nextSet] });
    };

    // The range (from/to) and the checkbox selection are mutually exclusive:
    // using one greys out and disables the other so it's clear only one applies.
    const rangeDisabled = selected.length > 0;
    const selectionDisabled = from !== "" || to !== "";

    const inputClass =
        "h-8 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 placeholder:text-muted-foreground/50 font-mono disabled:cursor-not-allowed disabled:opacity-50";

    return (
        <div className="flex min-w-0 flex-col space-y-3">
            <div className={cn("space-y-3", rangeDisabled && "opacity-50")}>
                <div className="space-y-1.5">
                    <span className="text-xs font-medium text-muted-foreground">From</span>
                    <input
                        type="text"
                        value={from}
                        onChange={(e) => commit({ from: e.target.value })}
                        placeholder="e.g. 1000"
                        className={inputClass}
                        disabled={rangeDisabled}
                        autoFocus
                        aria-label="From account number"
                    />
                </div>

                <div className="space-y-1.5">
                    <span className="text-xs font-medium text-muted-foreground">To</span>
                    <input
                        type="text"
                        value={to}
                        onChange={(e) => commit({ to: e.target.value })}
                        placeholder="e.g. 9999"
                        className={inputClass}
                        disabled={rangeDisabled}
                        aria-label="To account number"
                    />
                </div>
            </div>

            {options.length > 0 && (
                <div
                    className={cn(
                        "flex min-w-0 flex-col space-y-3",
                        selectionDisabled && "pointer-events-none opacity-50",
                    )}
                    aria-disabled={selectionDisabled}
                >
                    <div className="border-t border-border pt-2">
                        <span className="text-xs font-medium text-muted-foreground">
                            Or select values
                        </span>
                    </div>
                    {showSearch && (
                        <OptionSearchInput
                            value={optionQuery}
                            onChange={setOptionQuery}
                            autoFocus={false}
                        />
                    )}
                    {blank && (
                        <BlankOptionCheckbox
                            label={blank.label}
                            checked={isChecked(blank.value)}
                            disabled={selectionDisabled}
                            onToggle={() => toggle(blank.value)}
                        />
                    )}
                    {rest.length > 0 && (
                        <div className="flex max-h-[min(240px,40vh)] flex-col gap-2 overflow-y-auto overflow-x-hidden pr-1">
                            {visibleOptions.length === 0 ? (
                                <span className="py-1 text-sm text-muted-foreground">
                                    No matching options
                                </span>
                            ) : (
                                visibleOptions.map((option) => (
                                    <label
                                        key={option.value}
                                        className="flex cursor-pointer items-start gap-2 text-sm"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isChecked(option.value)}
                                            onChange={() => toggle(option.value)}
                                            disabled={selectionDisabled}
                                            className="border-input mt-0.5 shrink-0 rounded"
                                        />
                                        <span
                                            className="min-w-0 flex-1 break-words font-mono leading-snug"
                                            title={option.label}
                                        >
                                            {option.label}
                                        </span>
                                    </label>
                                ))
                            )}
                        </div>
                    )}
                </div>
            )}

            <div className="flex shrink-0 justify-end gap-2 border-t border-border pt-2">
                <button
                    type="button"
                    onClick={() => column.setFilterValue(undefined)}
                    className="px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
                >
                    Clear
                </button>
                <button
                    type="button"
                    onClick={onClose}
                    className="rounded bg-primary px-3 py-1 text-xs text-primary-foreground hover:bg-primary/90"
                >
                    Done
                </button>
            </div>
        </div>
    );
}

// Filter popover component
function FilterPopover({ column }: { column: Column<unknown, unknown> }) {
    const [open, setOpen] = React.useState(false);
    const meta = column.columnDef.meta;
    const rawFilter = column.getFilterValue();
    const hasActiveFilter =
        meta?.filterType === "date"
            ? isDateRangeFilterActive(rawFilter)
            : meta?.filterType === "numberRange"
              ? isNumberRangeMultiSelectFilterActive(rawFilter)
              : meta?.filterType === "multiSelect"
                ? Array.isArray(rawFilter) && rawFilter.length > 0
                : rawFilter !== undefined && rawFilter !== null && rawFilter !== "";

    if (!meta?.filterType) return null;

    const renderFilterContent = () => {
        const onClose = () => setOpen(false);

        switch (meta.filterType) {
            case "text":
                return (
                    <TextFilterContent
                        column={column}
                        placeholder={meta.filterPlaceholder}
                        onClose={onClose}
                        blankOption={meta.filterBlankOption}
                    />
                );
            case "select":
                return (
                    <SelectFilterContent
                        column={column}
                        options={meta.filterOptions ?? []}
                        placeholder={meta.filterPlaceholder}
                        onClose={onClose}
                    />
                );
            case "multiSelect":
                return (
                    <MultiSelectFilterContent
                        column={column}
                        options={meta.filterOptions ?? []}
                        onClose={onClose}
                        searchable={meta.filterSearchable}
                    />
                );
            case "date":
                return <DateFilterContent column={column} onClose={onClose} />;
            case "numberRange":
                return (
                    <NumberRangeMultiSelectFilterContent
                        column={column}
                        options={meta.filterOptions ?? []}
                        onClose={onClose}
                        searchable={meta.filterSearchable}
                    />
                );
            case "boolean":
                return (
                    <BooleanFilterContent
                        column={column}
                        placeholder={meta.filterPlaceholder}
                        onClose={onClose}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <Popover.Root open={open} onOpenChange={setOpen}>
            <Popover.Trigger asChild>
                <button
                    onClick={(e) => e.stopPropagation()}
                    className="p-0.5 rounded hover:bg-muted/80"
                    title="Filter"
                    {...(hasActiveFilter ? { "data-testid": `filter-active-${column.id}` } : {})}
                >
                    <Filter
                        className={cn(
                            "h-3.5 w-3.5 transition-opacity",
                            hasActiveFilter
                                ? "opacity-100 text-primary"
                                : "opacity-0 group-hover:opacity-100 text-muted-foreground",
                        )}
                    />
                </button>
            </Popover.Trigger>
            <Popover.Portal>
                <Popover.Content
                    className={cn(
                        "z-50 p-3 bg-popover border rounded-lg shadow-lg animate-in fade-in-0 zoom-in-95",
                        meta.filterType === "date"
                            ? "min-w-[280px] w-[min(100vw-1rem,360px)] max-h-[min(90vh,640px)] overflow-y-auto"
                            : meta.filterType === "multiSelect"
                              ? "min-w-[200px] max-w-[min(100vw-1rem,320px)] w-[min(100vw-1rem,280px)]"
                              : meta.filterType === "numberRange"
                                ? "min-w-[200px] max-w-[min(100vw-1rem,320px)] w-[min(100vw-1rem,280px)] max-h-[min(90vh,520px)] overflow-y-auto"
                                : "w-56",
                    )}
                    sideOffset={5}
                    align="end"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="text-xs font-medium text-muted-foreground mb-2">
                        Filter by {String(column.columnDef.header || column.id)}
                    </div>
                    {renderFilterContent()}
                    <Popover.Arrow className="fill-popover" />
                </Popover.Content>
            </Popover.Portal>
        </Popover.Root>
    );
}

function DataTableInner<TData, TValue>({
    columns,
    data,
    onRowClick,
    onFilteredDataChange,
    loading,
    emptyMessage = "No results.",
    className,
    enableFiltering = true,
    enableColumnResizing = true,
    storageKey,
    maxHeight,
    initialColumnFilters,
    onColumnFiltersChange: onColumnFiltersChangeProp,
    columnFilters: columnFiltersProp,
    enablePerColumnTextSearch = false,
    perColumnTextPlaceholder = "Filter…",
    pagination,
    rowCount,
    rowNoun = { one: "row", many: "rows" },
    showRowCount = true,
    manualFiltering = false,
    manualSorting = false,
    sortingState,
    onSortingChange,
    getSubRows,
    expanded,
    onExpandedChange,
    rowClassName,
    footerRow,
    renderRowBanner,
}: TDataTableProps<TData, TValue>) {
    // Controllable sorting: a consumer either drives sorting via `sortingState`
    // (kept stably defined) or omits it for internal state. It must not toggle
    // between defined and undefined after mount — `sortingInternal` is seeded
    // once and not re-synced.
    const [sortingInternal, setSortingInternal] = React.useState<SortingState>(sortingState ?? []);
    const sorting = sortingState ?? sortingInternal;
    const handleSortingChange = React.useCallback(
        (updater: SortingState | ((old: SortingState) => SortingState)) => {
            setSortingInternal((prev) => {
                const base = sortingState ?? prev;
                const next = typeof updater === "function" ? updater(base) : updater;
                onSortingChange?.(next);
                return next;
            });
        },
        [onSortingChange, sortingState],
    );
    // Expansion is always consumer-controlled: the caller owns which rows are open
    // (and usually the data behind them), so there is no internal fallback state.
    const handleExpandedChange = React.useCallback(
        (updater: ExpandedState | ((old: ExpandedState) => ExpandedState)) => {
            const base = expanded ?? {};
            onExpandedChange?.(typeof updater === "function" ? updater(base) : updater);
        },
        [expanded, onExpandedChange],
    );
    const [columnFiltersInternal, setColumnFiltersInternal] = React.useState<ColumnFiltersState>(
        () => columnFiltersProp ?? initialColumnFilters ?? [],
    );
    const [perColumnTextFilters, setPerColumnTextFilters] = React.useState<Record<string, string>>(
        {},
    );
    const [columnSizing, setColumnSizing] = React.useState<ColumnSizingState>(() =>
        loadColumnSizing(storageKey),
    );

    // Only persist once the user has actually resized a column. Hovering a grip
    // seeds widths (for a smooth first drag) but must not write to localStorage.
    const hasUserResizedRef = React.useRef(false);
    const markUserResized = React.useCallback(() => {
        hasUserResizedRef.current = true;
    }, []);

    // Double-clicking a column's resize handle fits that one column to its full
    // header label (measured from the DOM), so the column name shows in full.
    const resetColumnSize = React.useCallback((columnId: string) => {
        const width = measureHeaderFitWidth(tableRef.current, columnId);
        if (width == null) return;
        hasUserResizedRef.current = true;
        setColumnSizing((prev) => ({ ...prev, [columnId]: width }));
    }, []);

    // Persist column widths (debounced so a drag doesn't hammer localStorage).
    const debouncedColumnSizing = useDebounce(columnSizing, 300);
    React.useEffect(() => {
        if (!storageKey || !hasUserResizedRef.current) return;
        saveColumnSizing(storageKey, debouncedColumnSizing);
    }, [storageKey, debouncedColumnSizing]);

    // Flush the latest widths on unmount (or storageKey change) so a resize
    // followed by navigation within the debounce window isn't lost.
    const columnSizingRef = React.useRef(columnSizing);
    columnSizingRef.current = columnSizing;
    React.useEffect(() => {
        if (!storageKey) return;
        return () => {
            if (hasUserResizedRef.current) {
                saveColumnSizing(storageKey, columnSizingRef.current);
            }
        };
    }, [storageKey]);

    const tableRef = React.useRef<HTMLTableElement>(null);

    const handleDebouncedColumnText = React.useCallback((columnId: string, value: string) => {
        setPerColumnTextFilters((prev) => {
            if (prev[columnId] === value) return prev;
            return { ...prev, [columnId]: value };
        });
    }, []);

    const dataForTable = React.useMemo(() => {
        if (!enablePerColumnTextSearch) return data;
        const activeEntries = Object.entries(perColumnTextFilters).filter(
            ([, v]) => v.trim().length > 0,
        );
        if (activeEntries.length === 0) return data;
        return data.filter((row) =>
            activeEntries.every(([colId, q]) => {
                const needle = q.trim().toLowerCase();
                const def = findColumnDefById(columns, colId);
                if (!def) return true;
                const hay = getSearchHaystackFromDef(row, def).toLowerCase();
                return hay.includes(needle);
            }),
        );
    }, [data, perColumnTextFilters, enablePerColumnTextSearch, columns]);

    const setColumnFilters = React.useCallback(
        (updater: ColumnFiltersState | ((old: ColumnFiltersState) => ColumnFiltersState)) => {
            setColumnFiltersInternal((prev) => {
                const base = columnFiltersProp ?? prev;
                const next = typeof updater === "function" ? updater(base) : updater;
                onColumnFiltersChangeProp?.(next);
                return next;
            });
        },
        [onColumnFiltersChangeProp, columnFiltersProp],
    );

    const columnFilters = columnFiltersProp ?? columnFiltersInternal;
    const columnsForTable = useRangeFilterFns(columns, manualFiltering);

    const table = useReactTable({
        data: dataForTable,
        columns: columnsForTable,
        // Floor on resize width so columns can't be shrunk until content overlaps.
        defaultColumn: { minSize: 80 },
        getCoreRowModel: getCoreRowModel(),
        onSortingChange: handleSortingChange,
        getSortedRowModel: getSortedRowModel(),
        onColumnFiltersChange: setColumnFilters,
        getFilteredRowModel: getFilteredRowModel(),
        manualFiltering,
        manualSorting,
        // Nested rows. Inert unless a consumer passes `getSubRows`: with no
        // sub-rows the expanded row model is a pass-through over the core rows.
        getSubRows,
        getExpandedRowModel: getExpandedRowModel(),
        onExpandedChange: handleExpandedChange,
        // Column resizing
        enableColumnResizing,
        columnResizeMode: "onChange",
        onColumnSizingChange: setColumnSizing,
        state: {
            sorting,
            columnFilters,
            columnSizing,
            expanded: expanded ?? {},
        },
    });

    const effectiveRowCount = React.useMemo(() => {
        if (rowCount) return rowCount;
        const total = data.length;
        const filtered = table.getFilteredRowModel().rows.length;
        const isFiltered = columnFilters.length > 0 || dataForTable.length !== data.length;
        return { filtered, total, isFiltered };
    }, [rowCount, data.length, dataForTable.length, columnFilters, table]);

    const filteredLabel = formatCount(
        effectiveRowCount.filtered,
        rowCount?.filteredIsLowerBound ?? false,
    );
    const totalLabel = formatCount(effectiveRowCount.total, rowCount?.totalIsLowerBound ?? false);
    const countLabel = effectiveRowCount.isFiltered
        ? `Showing ${filteredLabel} of ${totalLabel}`
        : `${totalLabel} ${effectiveRowCount.total === 1 ? rowNoun.one : rowNoun.many}`;

    const showCountBar =
        showRowCount && !loading && (rowCount != null || (!manualFiltering && !manualSorting));
    const countBar = showCountBar ? (
        <div
            data-testid="datatable-row-count"
            className="flex justify-end px-1 pb-2 text-sm text-muted-foreground tabular-nums"
        >
            {countLabel}
        </div>
    ) : null;

    React.useEffect(() => {
        if (!onFilteredDataChange) {
            return;
        }
        const filteredRows = table.getFilteredRowModel().rows.map((row) => row.original);
        onFilteredDataChange(filteredRows);
    }, [onFilteredDataChange, dataForTable, columnFilters, sorting, table]);

    // Only use explicit sizing when columns have been resized
    const hasCustomSizing = Object.keys(columnSizing).length > 0;
    const scrollableStyle =
        maxHeight != null
            ? { maxHeight: typeof maxHeight === "number" ? `${maxHeight}px` : maxHeight }
            : undefined;

    const tableElement = (
        <table
            ref={tableRef}
            className="w-full text-sm"
            style={
                hasCustomSizing
                    ? {
                          // Once resized, fixed layout makes the explicit column widths
                          // authoritative (auto layout otherwise redistributes and cancels
                          // out the drag). minWidth keeps the table filling its container.
                          tableLayout: "fixed",
                          width: table.getCenterTotalSize(),
                          minWidth: "100%",
                      }
                    : undefined
            }
        >
            <thead
                className={cn(
                    maxHeight != null &&
                        "sticky top-0 z-10 bg-muted shadow-[0_1px_0_0_hsl(var(--border))]",
                )}
            >
                {table.getHeaderGroups().map((headerGroup, groupIndex, headerGroups) => (
                    <React.Fragment key={headerGroup.id}>
                        <tr className="border-b bg-muted/50">
                            {headerGroup.headers.map((header) => {
                                const isLeaf =
                                    !header.isPlaceholder && header.subHeaders.length === 0;
                                const canSort = isLeaf && header.column.getCanSort();
                                const canFilter =
                                    isLeaf &&
                                    enableFiltering &&
                                    header.column.columnDef.meta?.filterType;
                                const canResize =
                                    !header.isPlaceholder &&
                                    enableColumnResizing &&
                                    header.column.getCanResize();

                                return (
                                    <th
                                        key={header.id}
                                        data-column-id={header.column.id}
                                        colSpan={header.colSpan}
                                        className={cn(
                                            "px-4 py-3 text-left font-medium text-muted-foreground group relative",
                                            header.column.columnDef.meta?.headerClassName,
                                        )}
                                        style={
                                            hasCustomSizing
                                                ? { width: header.getSize() }
                                                : undefined
                                        }
                                    >
                                        {header.isPlaceholder ? null : (
                                            <div
                                                data-header-content
                                                className="flex items-center gap-1 pr-2"
                                            >
                                                <span data-header-label className="truncate">
                                                    {flexRender(
                                                        header.column.columnDef.header,
                                                        header.getContext(),
                                                    )}
                                                </span>
                                                {canSort && (
                                                    <button
                                                        onClick={header.column.getToggleSortingHandler()}
                                                        className="p-0.5 rounded hover:bg-muted/80"
                                                        title="Sort"
                                                    >
                                                        <SortIcon
                                                            direction={header.column.getIsSorted()}
                                                            isActive={
                                                                header.column.getIsSorted() !==
                                                                false
                                                            }
                                                        />
                                                    </button>
                                                )}
                                                {canFilter && (
                                                    <FilterPopover
                                                        column={
                                                            header.column as Column<
                                                                unknown,
                                                                unknown
                                                            >
                                                        }
                                                    />
                                                )}
                                            </div>
                                        )}
                                        {canResize && (
                                            <ColumnResizeHandle
                                                header={header}
                                                table={table}
                                                tableRef={tableRef}
                                                onResizeStart={markUserResized}
                                                onResetColumn={resetColumnSize}
                                            />
                                        )}
                                    </th>
                                );
                            })}
                        </tr>
                        {enablePerColumnTextSearch && groupIndex === headerGroups.length - 1 ? (
                            <tr className="border-b bg-muted/30">
                                {headerGroup.headers.map((header) => {
                                    const def = header.column.columnDef as ColumnDef<
                                        TData,
                                        unknown
                                    >;
                                    const showInput =
                                        !header.isPlaceholder && columnHasInlineTextSearch(def);
                                    return (
                                        <th
                                            key={`text-${header.id}`}
                                            className="px-2 py-1.5 align-top font-normal"
                                            style={
                                                hasCustomSizing
                                                    ? { width: header.getSize() }
                                                    : undefined
                                            }
                                        >
                                            {showInput ? (
                                                <PerColumnTextInput
                                                    columnId={header.column.id}
                                                    placeholder={perColumnTextPlaceholder}
                                                    onDebouncedChange={handleDebouncedColumnText}
                                                />
                                            ) : null}
                                        </th>
                                    );
                                })}
                            </tr>
                        ) : null}
                    </React.Fragment>
                ))}
            </thead>
            <tbody>
                {loading ? (
                    <tr>
                        <td
                            colSpan={table.getVisibleLeafColumns().length}
                            className="h-24 text-center text-muted-foreground"
                        >
                            Loading...
                        </td>
                    </tr>
                ) : table.getRowModel().rows?.length ? (
                    table.getRowModel().rows.map((row) => {
                        const banner = row.getIsExpanded()
                            ? renderRowBanner?.(row.original)
                            : undefined;
                        return (
                            <React.Fragment key={row.id}>
                                <tr
                                    className={cn(
                                        "border-b transition-colors even:bg-muted/40 hover:bg-muted/70",
                                        onRowClick && "cursor-pointer active:bg-muted/90",
                                        rowClassName?.(row.original, row.depth),
                                    )}
                                    onClick={() => onRowClick?.(row.original)}
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <td
                                            key={cell.id}
                                            className={cn(
                                                "px-4 py-3",
                                                // Once sized (fixed layout), clip overflow so a narrow
                                                // column truncates instead of overlapping its neighbour.
                                                hasCustomSizing && "truncate",
                                            )}
                                            style={
                                                hasCustomSizing
                                                    ? { width: cell.column.getSize() }
                                                    : undefined
                                            }
                                        >
                                            {flexRender(
                                                cell.column.columnDef.cell,
                                                cell.getContext(),
                                            )}
                                        </td>
                                    ))}
                                </tr>
                                {banner ? (
                                    <tr className="border-b">
                                        <td
                                            colSpan={table.getVisibleLeafColumns().length}
                                            className="p-0"
                                        >
                                            {banner}
                                        </td>
                                    </tr>
                                ) : null}
                            </React.Fragment>
                        );
                    })
                ) : (
                    <tr>
                        <td
                            colSpan={table.getVisibleLeafColumns().length}
                            className="h-24 text-center text-muted-foreground"
                        >
                            {emptyMessage}
                        </td>
                    </tr>
                )}
            </tbody>
            {footerRow ? (
                <tfoot
                    className={cn(
                        "border-t-2",
                        maxHeight != null &&
                            "sticky bottom-0 z-10 bg-muted shadow-[0_-1px_0_0_hsl(var(--border))]",
                    )}
                >
                    {footerRow}
                </tfoot>
            ) : null}
        </table>
    );

    if (maxHeight != null) {
        return (
            <div className={cn("rounded-md", className)}>
                {countBar}
                <div className="overflow-auto" style={scrollableStyle}>
                    {tableElement}
                </div>
                {pagination && <Pagination {...pagination} className="px-4 py-3 border-t" />}
            </div>
        );
    }

    return (
        <div className={cn("rounded-md", className)}>
            {countBar}
            {/* Contain horizontal overflow here so a wide table scrolls within its
                own box instead of scrolling the whole page sideways. Pagination
                stays outside the scroll area. */}
            <div className="overflow-x-auto">{tableElement}</div>
            {pagination && <Pagination {...pagination} className="px-4 py-3 border-t" />}
        </div>
    );
}

export const DataTable = React.memo(DataTableInner) as typeof DataTableInner;
